import axios from "axios";

const BASE_URL = process.env.REACT_APP_PITS_BASE_URL;

const MILB_SPORT_IDS = [11, 12, 13, 14];

const getMode = (sportId) => {
  return MILB_SPORT_IDS.includes(sportId) ? "MILB" : "MLB";
};

class PitsApi {
  getQueryInfo(query) {
    return axios
      .post(`${BASE_URL}/search/info`, {
        queries: [query],
        searchType: "UNIFIED",
        queryType: "STRUCTURED",
        languagePreference: "EN"
      })
      .then(r => r.data);
  }

  search(q, limit = 10, page = 0) {
    return axios
      .get(`${BASE_URL}/search`, { params: { q, limit, page } })
      .then(r => r.data);
  }

  getSynonyms(page = 0, limit = 20) {
    return axios
      .get(`${BASE_URL}/fts/synonyms?page=${page}&limit=${limit}`)
      .then(r => r.data);
  }

  deleteSynonym(value, synonym) {
    return axios.delete(`${BASE_URL}/fts/synonyms/${value}/${synonym}`);
  }

  addSynonym(value, synonym) {
    return axios.put(`${BASE_URL}/fts/synonyms/add/${value}/${synonym}`);
  }

  migrateSynonyms() {
    return axios.post(`${BASE_URL}/fts/synonyms/migrate`).then(r => r.data);
  }

  getSynonymsDiff() {
    return axios.get(`${BASE_URL}/fts/synonyms/diff`).then(r => r.data);
  }

  getFields(searchType = "UNIFIED") {
    return axios
      .get(`${BASE_URL}/metadata/fields?searchType=${searchType}`)
      .then(r => r.data);
  }

  getFieldValues(fieldId, searchType = "UNIFIED") {
    return axios
      .get(
        `${BASE_URL}/metadata/fields/${fieldId}/values?searchType=${searchType}`
      )
      .then(r => r.data);
  }

  getTypes() {
    return axios.get(`${BASE_URL}/metadata/types`).then(r => r.data);
  }

  getPlayInfo(playId) {
    return axios
      .get(`${BASE_URL}/plays/${playId}`)
      .then(r => r.data)
      .then(play => {
        const playInfo = play.playInfo;
        const postState = playInfo?.gameFacts?.gameState?.post;

        return {
          PlayId: play.id || playId,
          StartTime: playInfo?.playMetaData?.startTime || "",
          PitchTime: playInfo?.playMetaData?.pitchTime || "",
          EndTime: playInfo?.playMetaData?.endTime || "",
          GamePk: playInfo?.gameFacts?.gamePk || "",
          HomeTeamRuns: postState?.home?.runs ?? "",
          AwayTeamRuns: postState?.away?.runs ?? "",
          Mode: getMode(playInfo?.gameFacts?.home?.sport?.id)
        };
      })
      .catch(() => {
        return {
          PlayId: playId,
          StartTime: "",
          PitchTime: "",
          EndTime: "",
          GamePk: "",
          HomeTeamRuns: "",
          AwayTeamRuns: "",
          Mode: "MLB"
        };
      });
  }

  getPinnedSearchSuggestions() {
    return axios
      .get(`${BASE_URL}/fts/search/suggestions/type/pinned`)
      .then(r => r.data);
  }

  getRandomizedSearchSuggestions() {
    return axios
      .get(`${BASE_URL}/fts/search/suggestions/type/randomized`)
      .then(r => r.data);
  }

  addPinnedSearchSuggestion(suggestedSearch, sortOrder) {
    return axios
      .post(`${BASE_URL}/fts/search/suggestions/pinned`, {
        suggestedSearch,
        sortOrder
      })
      .then(r => r.data);
  }

  addRandomizedSearchSuggestion(suggestedSearch) {
    return axios
      .post(`${BASE_URL}/fts/search/suggestions/randomized/${suggestedSearch}`)
      .then(r => r.data);
  }

  deleteSearchSuggestion(id) {
    return axios.delete(`${BASE_URL}/fts/search/suggestions/${id}`);
  }

  reorderSearchSuggestions(reorderList) {
    return axios
      .put(`${BASE_URL}/fts/search/suggestions/reorder`, {
        reorderList
      })
      .then(r => r.data);
  }

  getMacros() {
    return axios.get(`${BASE_URL}/fts/macros`).then(r => r.data);
  }

  getMacro(macroId) {
    return axios.get(`${BASE_URL}/fts/macros/${macroId}`).then(r => r.data);
  }

  insertMacro(macro) {
    return axios
      .post(`${BASE_URL}/fts/macros`, { macros: [macro] })
      .then(r => r.data);
  }

  updateMacro(id, macro) {
    return axios
      .put(`${BASE_URL}/fts/macros/${id}`, { ...macro })
      .then(r => r.data);
  }

  deleteMacro(id) {
    return axios.delete(`${BASE_URL}/fts/macros/${id}`).then(r => r.data);
  }
}

PitsApi.BASE_URL = BASE_URL;

export default PitsApi;

export const getPlayDoc = playId => {
  return `${BASE_URL}/plays/${playId}`;
};
